import * as React from "react"
import { useState, useEffect } from "react"
import { subscribeToUser, updateUserData } from "../business_logic/users"
import { getUserSubscription } from "../business_logic/subscriptions"
import { User, UserSubscription } from "../business_logic/model"
import { useAuth } from "../business_logic/authentication"
import { initFirebase } from "../business_logic/firebase"
import config from "../config.json"

const analytics = initFirebase()

interface AuthContextProps {
  user: User
  subscriptionData: UserSubscription
  mainLoading: boolean
}

export const AuthContext = React.createContext<AuthContextProps>({
  user: null,
  subscriptionData: null,
  mainLoading: true,
})

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(
    localStorage.getItem("inspiring-chats-user")
      ? JSON.parse(localStorage.getItem("inspiring-chats-user"))
      : null,
  )
  const [subscriptionData, setSubscriptionData] = useState(null)
  const [mainLoading, setMainLoading] = useState(true)

  useEffect(() => {
    if (typeof window !== "undefined") {
      useAuth(async firebaseUser => {
        if (firebaseUser) {
          try {
            await updateUserData(firebaseUser.uid, {
              lastActive: Number(firebaseUser.metadata.lastLoginAt),
            })
          } catch (e) {
            // pass - when a user first creates their account the document may not be created yet at this point
          }
          const unsubscribe = subscribeToUser(firebaseUser, setUser, console.log)
          setMainLoading(false)
          return unsubscribe
        } else {
          localStorage.removeItem("inspiring-chats-user")
        }
      })
    }
  }, [])

  useEffect(() => {
    if (user && config.show_membership) {
      return getUserSubscription(
        user.id,
        subscription => {
          setSubscriptionData(subscription)
          const userData = { ...user }
          userData.hasSubscription = true
          setUser(userData)
        },
        () => {
          const userData = { ...user }
          userData.hasSubscription = false
          setUser(userData)
        },
      )
    }
  }, [user?.id])

  useEffect(() => {
    const timer = setTimeout(() => {
      setMainLoading(false)
    }, 1500)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    if (user) {
      const { email, contactPhone, contactEmail, ...noSensitiveDataUser } = user
      localStorage.setItem("inspiring-chats-user", JSON.stringify(noSensitiveDataUser))
    }
  }, [user])

  return (
    <AuthContext.Provider value={{ user, subscriptionData, mainLoading }}>
      {children}
    </AuthContext.Provider>
  )
}
